/**
 * Admin API client.
 *
 * Wraps the /api/admin/users endpoints. Used by the UserManagement route.
 * Auth rides on the HttpOnly cookie set during Cognito login (same as the
 * other lib/*.ts clients).
 *
 * Server-side dependencies: see apps/api/ashi_api/api/users.py.
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL || "";

function jsonHeaders(): HeadersInit {
  return {
    "Content-Type": "application/json",
  };
}

async function jsonOrError<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let detail = `HTTP ${response.status}`;
    try {
      const body = await response.json();
      if (body?.detail) detail = body.detail;
    } catch {
      // ignore — surface the status code only
    }
    throw new Error(detail);
  }
  // 204 responses have no body
  if (response.status === 204) {
    return undefined as unknown as T;
  }
  return response.json();
}

// ---- Types matching the server's Pydantic models ----

export interface UserSummary {
  username: string;
  email: string;
  enabled: boolean;
  /** Cognito UserStatus. The key ones in practice:
   *   FORCE_CHANGE_PASSWORD  — invited, not yet logged in
   *   CONFIRMED              — active
   *   RESET_REQUIRED         — needs to reset password
   */
  status: string;
  groups: string[];
  is_admin: boolean;
  has_ai_analyst: boolean;
  /** True = unrestricted (all dashboards). True for admins regardless of the
   *  stored allowlist. When false, `dashboards` lists the allowed ids. */
  all_dashboards: boolean;
  /** Allowed dashboard ids when `all_dashboards` is false; empty otherwise. */
  dashboards: string[];
  created_at: string | null;
  last_modified_at: string | null;
}

export interface UserListResponse {
  users: UserSummary[];
  total: number;
}

export interface CreateUserRequest {
  email: string;
  given_name?: string;
  family_name?: string;
  /** Initial group memberships. Empty = viewers-only. */
  groups: Array<"admins" | "ai_analyst">;
  /** Dashboard ids the user may see. Omit / null / ["*"] = all dashboards. */
  dashboards?: string[] | null;
  /** When true, create the account but send no invite email. Send it later
   *  with resendInvite(). Defaults to false (invite sent on create). */
  suppress_invite?: boolean;
}

export interface PatchUserRequest {
  /** Complete desired set of toggleable group memberships. Anything in this
   *  list will be ON; anything else OFF. Omit to leave groups unchanged. */
  groups?: Array<"admins" | "ai_analyst">;
  /** Dashboard allowlist. ["*"] = all access; a concrete list restricts.
   *  Omit (undefined) to leave the allowlist unchanged. */
  dashboards?: string[] | null;
}

// ---- Calls ----

export async function listUsers(): Promise<UserListResponse> {
  const r = await fetch(`${API_BASE}/api/admin/users`, {
    credentials: "include",
    headers: jsonHeaders(),
  });
  return jsonOrError<UserListResponse>(r);
}

export async function createUser(body: CreateUserRequest): Promise<UserSummary> {
  const r = await fetch(`${API_BASE}/api/admin/users`, {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders(),
    body: JSON.stringify(body),
  });
  return jsonOrError<UserSummary>(r);
}

export async function getUser(username: string): Promise<UserSummary> {
  const r = await fetch(
    `${API_BASE}/api/admin/users/${encodeURIComponent(username)}`,
    {
      credentials: "include",
      headers: jsonHeaders(),
    },
  );
  return jsonOrError<UserSummary>(r);
}

export async function patchUser(
  username: string,
  body: PatchUserRequest,
): Promise<UserSummary> {
  const r = await fetch(
    `${API_BASE}/api/admin/users/${encodeURIComponent(username)}`,
    {
      method: "PATCH",
      credentials: "include",
      headers: jsonHeaders(),
      body: JSON.stringify(body),
    },
  );
  return jsonOrError<UserSummary>(r);
}

export async function resendInvite(username: string): Promise<void> {
  const r = await fetch(
    `${API_BASE}/api/admin/users/${encodeURIComponent(username)}/resend-invite`,
    {
      method: "POST",
      credentials: "include",
      headers: jsonHeaders(),
    },
  );
  await jsonOrError<void>(r);
}

export async function disableUser(username: string): Promise<void> {
  const r = await fetch(
    `${API_BASE}/api/admin/users/${encodeURIComponent(username)}/disable`,
    {
      method: "POST",
      credentials: "include",
      headers: jsonHeaders(),
    },
  );
  await jsonOrError<void>(r);
}

export async function enableUser(username: string): Promise<void> {
  const r = await fetch(
    `${API_BASE}/api/admin/users/${encodeURIComponent(username)}/enable`,
    {
      method: "POST",
      credentials: "include",
      headers: jsonHeaders(),
    },
  );
  await jsonOrError<void>(r);
}

// ---- Helpers ----

/** Human-readable label for a Cognito UserStatus. */
export function statusLabel(status: string): string {
  switch (status) {
    case "CONFIRMED":
      return "Active";
    case "FORCE_CHANGE_PASSWORD":
      return "Invited";
    case "RESET_REQUIRED":
      return "Reset required";
    case "ARCHIVED":
      return "Archived";
    case "COMPROMISED":
      return "Compromised";
    case "UNKNOWN":
    default:
      return status;
  }
}
